import React, { Component } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import trackingLinkUnifiedOptions from '../data/tracking_link_unified.json';

const groupedOptions = trackingLinkUnifiedOptions.reduce((acc, option) => {
  const group = acc.find(g => g.label === option.optgroup);
  if (group) {
    group.options.push(option);
  } else {
    acc.push({ label: option.optgroup, options: [option] });
  }
  return acc;
}, []);

class TrackingInfoModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedServiceKey: "",
      trackingNumber: "",
      trackingUrl: "",
      trackingUrlPlaceholder: "",
      trackingCompany: "",
      isLoading: false,
      file: undefined,
      error: undefined,
      deliveryType: "",
      selfDelivered: false,
    }
  }

  handleOnInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
      error: undefined,
    });
  }

  handleOnSelectChange = (e) => {
    const selectedOption = e.target.selectedOptions[0];
    if (!selectedOption) return;

    const deliveryType = selectedOption.dataset.deliveryType;
    const selfDelivered = selectedOption.dataset.selfDelivered === 'true';
    const name = selectedOption.dataset.name;
    const url = selectedOption.dataset.url;

    const state = {
      selectedServiceKey: e.target.value,
      deliveryType,
      selfDelivered,
      trackingCompany: name,
      trackingNumber: "",
      trackingUrl: "",
      trackingUrlPlaceholder: "",
      error: undefined,
    };

    if (deliveryType === 'courier') {
      state.trackingUrl = url;
    } else if (deliveryType === 'd2d' && !selfDelivered) {
      state.trackingUrlPlaceholder = url;
    }

    this.setState(state);
  }

  handleOnClickFulfill = (e) => {
    e.preventDefault();
    const { onClickFulfill } = this.props;
    const { trackingCompany, trackingNumber, trackingUrl, file, deliveryType, selfDelivered } = this.state;

    // Required field validation
    if (!deliveryType) {
      this.setState({ error: 'Please select a delivery service' });
      return;
    }

    if (deliveryType === 'courier') {
      if (!trackingNumber.trim()) {
        this.setState({ error: 'Tracking Number is required' });
        return;
      }
      if (!trackingUrl.trim()) {
        this.setState({ error: 'Tracking URL is required' });
        return;
      }
    }

     if (deliveryType === 'd2d' && !selfDelivered) {
      if (!trackingUrl.trim()) {
        this.setState({ error: 'Tracking URL is required' });
        return;
      }
    }

    if (selfDelivered && deliveryType !== 'pickup' && !file) {
      this.setState({ error: 'Proof of Delivery is required' });
      return;
    }

    this.setState({ isLoading: true, error: undefined });

    const finalTrackingUrl = selfDelivered ? "" : trackingUrl;

    onClickFulfill(deliveryType, trackingCompany, trackingNumber, finalTrackingUrl, file);
  }

  handleOnClose = () => {
    const { onClose } = this.props;
    const { isLoading } = this.state;
    if (!isLoading) {
      onClose();
    }
  }

  addFile = e => {
    const { files } = e.target;

    if (files && files.length > 0 && !files[0].type) {
      this.setState({ error: 'Invalid file extension' });
      return;
    }
    this.setState({
      file: files && files.length > 0 ? files[0] : undefined,
      error: undefined,
    });
  }

  render() {
    const { okLabel, isMobile } = this.props;
    const {
      selectedServiceKey, trackingNumber, trackingUrl, isLoading,
      file, error, deliveryType, selfDelivered, trackingUrlPlaceholder,
    } = this.state;

    return (
      <Modal dialogClassName={`${isMobile ? "" : "tracking-info-modal-dialog"}`} style={{border: 'none'}} show={true} onHide={this.handleOnClose}>
        <form onSubmit={this.handleOnClickFulfill}>
          <Modal.Header className="mx-2 mt-1" style={{borderBottom: 'none'}} closeButton>
            <h4 className="mb-0">Tracking Information</h4>
          </Modal.Header>
          <Modal.Body className="mx-4 px-0 py-4" style={{borderTop: '1px solid #dee2e6'}}>
            {
              error &&
                <Alert variant="danger">
                  {
                    Array.isArray(error) ?
                      <ul>
                        {
                          error.map((err, i) => (
                            <li key={i}>
                              {err.msg}
                            </li>
                          ))
                        }
                      </ul>
                      :
                      error
                  }
                </Alert>
            }
            <div className="d-grid gap-3">
              <div className="bg-light p-3 rounded" style={{fontSize: '0.875rem'}}>
                <strong>Instructions:</strong>
                <ol className="mb-0 mt-1 ps-3">
                  <li>For sellers that are using courier logistic (DHL, GDEX, Poslaju etc.), tracking link and tracking number are required while marking the order as fulfilled.</li>
                  <li>For sellers that are using on-demand logistic (Lalamove, GrabExpress), real-time tracking link is required to mark the order as fulfilled once the item has been picked up.</li>
                  <li>For sellers that are using own riders/drivers for on-demand delivery orders, please select <strong>'Other On-Demand Logistic Service'</strong> and upload POD as proof so that customer gets notified instantly.</li>
                </ol>
              </div>
              <div className="d-grid gap-1">
                <label htmlFor="deliveryService">Select Delivery Service:</label>
                <Form.Select
                  id="deliveryService"
                  name="deliveryService"
                  className="form-control"
                  value={selectedServiceKey}
                  onChange={this.handleOnSelectChange}
                >
                  <option disabled value="">Select one</option>
                  {
                    groupedOptions.map(group => (
                      <optgroup label={group.label} key={group.label}>
                        {
                          group.options.map(option => (
                            <option
                              key={option.key}
                              value={option.key}
                              data-delivery-type={option.deliveryType}
                              data-self-delivered={String(option.selfDelivered)}
                              data-name={option.name}
                              data-url={option.value}
                            >
                              {option.name}
                            </option>
                          ))
                        }
                      </optgroup>
                    ))
                  }
                </Form.Select>
              </div>
              {
                deliveryType === 'courier' &&
                  <>
                    <div className="d-grid gap-1">
                      <label htmlFor="trackingNumber">Tracking Number <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" id="trackingNumber" name="trackingNumber" placeholder="Tracking Number" value={trackingNumber} onChange={this.handleOnInputChange} />
                    </div>
                    <div className="d-grid gap-1">
                      <label htmlFor="trackingUrl">Tracking URL <span className="text-danger">*</span></label>
                      <input onFocus={e => e.target.select()} type="text" className="form-control" id="trackingUrl" name="trackingUrl" placeholder="Tracking URL" value={trackingUrl} onChange={this.handleOnInputChange} />
                    </div>
                  </>
              }
              {
                (deliveryType === 'd2d' && !selfDelivered) &&
                  <div className="d-grid gap-1">
                    <label htmlFor="trackingUrl">Tracking URL <span className="text-danger">*</span></label>
                    <input onFocus={e => e.target.select()} type="text" className="form-control" id="trackingUrl" name="trackingUrl" placeholder={trackingUrlPlaceholder || "Tracking URL"} value={trackingUrl} onChange={this.handleOnInputChange} />
                  </div>
              }
              {
                selfDelivered &&
                  <div className="d-grid gap-1">
                    <label htmlFor="proof">
                      {
                        deliveryType === 'pickup' ?
                          'Proof of Collection or Image (Optional)'
                          :
                          <>Proof of Delivery or Image <span className="text-danger">*</span></>
                      }
                    </label>
                    {
                      deliveryType !== 'pickup' &&
                        <small>POD required to ensure sender gets notified regarding delivery updates with proof</small>
                    }
                    <div>
                      <label className="custom-file-upload me-2" style={{width: '35%'}}>
                        <FontAwesomeIcon icon={solid('folder-open')} className="me-2"/>Choose File
                        <input type="file" name="proof" onChange={this.addFile} />
                      </label>
                      {
                        file &&
                          <span>{file.name}</span>
                      }
                    </div>
                  </div>
              }
            </div>
          </Modal.Body>
          <Modal.Footer className="pt-0" style={{borderTop: 'none'}}>
            <Button variant="danger-secondary" onClick={this.handleOnClose} disabled={isLoading}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={isLoading || !deliveryType}>
              {isLoading ? "Loading..." : okLabel || "Save"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

export default TrackingInfoModal;
